<template>
    <ModalContent stitch
        :help-content-id="`popup-addcontractline-${current_entity_profile}`"
        class="px-0"
    >
        <template slot="title">{{ $translate("title") }}</template>
        <template slot="rawcontent">
            <div class="add-to-catalog-modal">
                <template v-if="stats.products">
                    <template v-if="step === 'select'">
                        <div class="px-4 pt-3">
                            <Info :elevation="1">
                                {{ $translate("importProductsDescription") }}
                            </Info>
                        </div>
                        <tabs
                            class="add-to-catalog-modal__tabs"
                            :value="tab"
                            :tabs="tabs"
                            @change="changeTab($event)"
                        >
                            <template slot="tabs">
                                <v-tab-item
                                    :key="tab.name"
                                    v-for="tab in tabs"
                                    class="py-2"
                                >
                                    <Products v-if="tab.name == 'products'" />
                                    <SelectedItems
                                        v-else-if="tab.name == 'selected'"
                                    />
                                </v-tab-item>
                            </template>
                        </tabs>
                    </template>
                    <template v-else-if="step === 'piece_content'">
                        <div class="px-4 pt-4 pb-2">
                            <div class="d-flex align-center flex-wrap mb-2">
                                <v-btn
                                    text
                                    small
                                    class="px-0 mr-3"
                                    @click="backToSelection"
                                >
                                    <v-icon left small>mdi-arrow-left</v-icon>
                                    {{ $translate("backToSelection") }}
                                </v-btn>
                                <v-chip
                                    small
                                    label
                                    color="primary"
                                    outlined
                                    class="font-weight-medium"
                                >
                                    {{ $translate("stepPieceContent") }}
                                </v-chip>
                            </div>
                        </div>
                        <div class="px-4 pb-4">
                            <PieceContentStep />
                        </div>
                    </template>
                </template>
                <template v-else>
                    <Placeholder
                        icon="product"
                        class="pa-4"
                        :title="$translate('noProducts')"
                        :description="$translate('noProductsDescription')"
                    />
                </template>
            </div>
        </template>

        <template slot="bottom">
            <template v-if="stats.products">
                <v-btn
                    class="my-2 my-sm-3 w-100 w-sm-auto"
                    :disabled="isBusy"
                    @click="onCancel"
                >
                    {{ $translate("cancel") }}
                </v-btn>

                <v-btn
                    v-if="step === 'select'"
                    class="my-2 my-sm-3 w-100 w-sm-auto ml-sm-3"
                    :disabled="isBusy"
                    :loading="adding_products"
                    color="primary"
                    @click="continueClicked"
                >
                    {{ continue_text }}
                </v-btn>
                <v-btn
                    v-else-if="step === 'piece_content'"
                    class="my-2 my-sm-3 w-100 w-sm-auto ml-sm-3"
                    :disabled="isBusy"
                    :loading="saving_piece_content || adding_products"
                    color="primary"
                    @click="saveAndAddClicked"
                >
                    {{ saveAndAdd_text }}
                </v-btn>
            </template>
            <template v-else>
                <v-btn
                    class="my-2 my-sm-4 w-100 w-sm-auto"
                    color="primary"
                    @click="hideModal"
                >
                    {{ $translate("close") }}
                </v-btn>
            </template>
        </template>
    </ModalContent>
</template>

<script>
import { mapState, mapGetters, mapActions } from "vuex";

import GlobalMixins from "@/mixins/global";
import Products from "./addtocatalog-products";
import SelectedItems from "./addtocatalog-selected";
import PieceContentStep from "./addtocatalog-piececontent";

const MODAL_WIDTH = 1200;

export default {
    maxWidth: "96%",
    width: MODAL_WIDTH,
    mixins: [GlobalMixins],
    components: { Products, SelectedItems, PieceContentStep },
    props: {
        params: {
            type: Object,
            required: false,
            default: null,
        },
    },
    data() {
        return {
            tab: "products",
        };
    },
    computed: {
        ...mapGetters("session", ["stats", "stats_loaded"]),
        ...mapGetters("addtocatalog", ["itemsNeedingPieceContent"]),
        ...mapState("addtocatalog", [
            "selected_items",
            "adding_products",
            "saving_piece_content",
            "step",
        ]),
        isBusy() {
            return this.adding_products || this.saving_piece_content;
        },
        tabs() {
            return [
                {
                    name: "products",
                    label: this.$translate("products"),
                },
                {
                    name: "selected",
                    label: this.$translate("selectedProducts"),
                    counter: this.selected_items.length,
                    disabled: this.selected_items.length == 0,
                },
            ];
        },
        continue_text() {
            if (this.selected_items.length === 0) {
                return this.$translate("addNothing");
            }
            if (this.itemsNeedingPieceContent.length > 0) {
                return this.$translate("continueToPieceContent", {
                    count: this.selected_items.length,
                });
            }
            if (this.selected_items.length > 1) {
                return this.$translate("addItems", {
                    count: this.selected_items.length,
                });
            }
            return this.$translate("addItem", {
                count: this.selected_items.length,
            });
        },
        saveAndAdd_text() {
            if (this.selected_items.length > 1) {
                return this.$translate("saveAndAddItems", {
                    count: this.selected_items.length,
                });
            }
            return this.$translate("saveAndAddItem", {
                count: this.selected_items.length,
            });
        },
    },
    methods: {
        ...mapActions("addtocatalog", [
            "init",
            "proceedToAdd",
            "savePieceContentAndImport",
            "goBackToSelection",
        ]),

        changeTab(tab) {
            this.tab = tab;
        },

        onCancel() {
            this.hideModal();
        },

        backToSelection() {
            this.goBackToSelection();
        },

        async continueClicked() {
            if (this.selected_items.length === 0) {
                this.hideModal({ result: { saved: false, skipped: true } });
                return;
            }
            try {
                const result = await this.proceedToAdd();
                if (result.step === "done") {
                    this.addAlert(this.$translate("productsAdded"));
                    this.hideModal({ result: { saved: true } });
                }
            } catch (error) {
                this.handleError(error);
            }
        },

        async saveAndAddClicked() {
            try {
                await this.savePieceContentAndImport();
                this.addAlert(this.$translate("productsAdded"));
                this.hideModal({ result: { saved: true } });
            } catch (error) {
                this.handleError(error);
            }
        },

        handleError(error) {
            if (error.validationErrors?.length) {
                if (error.validationType === "pieceContent") {
                    this.$swal({
                        title: this.$translate("pieceContentValidationFailed"),
                        text: this.$translate("pieceContentValidationHint"),
                        type: "warning",
                    });
                    return;
                }
                const lines = error.validationErrors.map((item) => {
                    if (item.messageKey) {
                        return `${item.productName}: ${this.$translate(
                            item.messageKey,
                        )}`;
                    }
                    return item.productName;
                });
                this.$swal({
                    title: this.$translate("validationFailed"),
                    html: lines.join("<br>"),
                    type: "warning",
                });
                return;
            }
            this.$swal({
                title: this.$translate("addingProductsFailed"),
                html: this.$utils.resultToSwalContent(this, error),
                type: "error",
            });
        },
    },

    created() {
        this.init(this.params);
    },

    i18n: {
        messages: {
            en: {
                productsAdded: "Product(s) added",
                addingProductsFailed: "Adding products failed",
                title: "Add products to catalog",
                cancel: "Cancel",
                addNothing: "Add nothing",
                addItem: "Add {count} product",
                addItems: "Add {count} products",
                continueToPieceContent: "Continue ({count} products)",
                saveAndAddItem: "Save and add {count} product",
                saveAndAddItems: "Save and add {count} products",
                products: "Products",
                selectedProducts: "Selected products",
                importProductsDescription:
                    "Select products to add. Products already in the catalog can be added again as variants with a sale format.",
                validationFailed: "Cannot add selected products",
                formatRequiredForVariant:
                    "pick a sale format to add a variant",
                saleFormatRequiredForVariant:
                    "pick a sale format to add a variant",
                pieceContentValidationFailed: "Piece content incomplete",
                pieceContentValidationHint:
                    "Fill in the content per piece for every product listed.",
                backToSelection: "Back to selection",
                stepPieceContent: "Step 2 · Piece content",
                close: "Close",
                noProducts: "No products available",
                noProductsDescription:
                    "Your sales product database is empty. Please create products to add them to your catalog.",
            },
            fr: {
                productsAdded: "Produit(s) ajouté(s)",
                addingProductsFailed: "Erreur pendant l'ajout des produits",
                title: "Ajouter des produits au catalogue",
                cancel: "Annuler",
                addNothing: "Ne rien ajouter",
                addItem: "Ajouter {count} produit",
                addItems: "Ajouter {count} produits",
                continueToPieceContent: "Continuer ({count} produits)",
                saveAndAddItem: "Enregistrer et ajouter {count} produit",
                saveAndAddItems: "Enregistrer et ajouter {count} produits",
                products: "Produits",
                selectedProducts: "Produits sélectionnés",
                importProductsDescription:
                    "Sélectionnez les produits à ajouter. Les produits déjà au catalogue peuvent être ajoutés à nouveau comme déclinaisons avec un format de vente.",
                validationFailed: "Impossible d'ajouter les produits sélectionnés",
                formatRequiredForVariant:
                    "choisissez un format de vente pour ajouter une déclinaison",
                saleFormatRequiredForVariant:
                    "choisissez un format de vente pour ajouter une déclinaison",
                pieceContentValidationFailed: "Contenu par pièce incomplet",
                pieceContentValidationHint:
                    "Renseignez le contenu par pièce pour chaque produit listé.",
                backToSelection: "Retour à la sélection",
                stepPieceContent: "Étape 2 · Contenu par pièce",
                close: "Fermer",
                noProducts: "Aucun produit disponible",
                noProductsDescription:
                    "Votre base de produits de vente est vide. Veuillez créer des produits pour les ajouter à votre catalogue.",
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.add-to-catalog-modal {
    ::v-deep .add-to-catalog-modal__tabs .v-tabs {
        padding: 0 16px;
    }

    ::v-deep .add-to-catalog-table.tablewrapper {
        .controls {
            padding-left: 16px !important;
            padding-right: 16px !important;
            padding-top: 0 !important;
        }

        .v-data-table > .v-data-table__wrapper > table > thead > tr > th:first-child,
        .v-data-table > .v-data-table__wrapper > table > tbody > tr > td:first-child {
            padding-left: 16px !important;
        }

        .v-data-table > .v-data-table__wrapper > table > thead > tr > th:last-child,
        .v-data-table > .v-data-table__wrapper > table > tbody > tr > td:last-child {
            padding-right: 16px !important;
        }
    }
}
</style>

